import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import PlanCard from "../components/PlanCard";
import { AuthContext } from "../context/AuthContext";
import { listPlans, setMyTier } from "../api/plansApi";
import { TIER_LABEL } from "../config/tiers";

// Shown right after signup. Basic is pre-selected and the step is fully
// skippable — no plan switch happens unless the user picks a paid tier, and
// switching is free for now. Reuses the same PlanCard as /pricing.
const ChoosePlan = () => {
  const { isAuthenticated, loading, refreshProfile } = useContext(AuthContext);
  const navigate = useNavigate();

  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [selectedTier, setSelectedTier] = useState("basic");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Guard: this screen only makes sense for a freshly-authed user.
  useEffect(() => {
    if (!loading && !isAuthenticated) navigate("/", { replace: true });
  }, [loading, isAuthenticated, navigate]);

  useEffect(() => {
    (async () => {
      try {
        setPlans(await listPlans());
      } catch {
        setError("Couldn't load plans — you can pick one later from Pricing.");
      } finally {
        setLoadingPlans(false);
      }
    })();
  }, []);

  const onSelect = (plan) => {
    // Advisor is a sales-led track, not a self-serve onboarding choice.
    if (plan.cta_type === "contact_sales") {
      navigate("/contact");
      return;
    }
    setSelectedTier(plan.tier_key);
  };

  const ctaLabel = (plan) => {
    if (plan.cta_type === "contact_sales") return "Talk to sales";
    if (selectedTier === plan.tier_key) return "Selected ✓";
    return "Select";
  };

  const proceed = async (tier) => {
    setError("");
    setBusy(true);
    try {
      if (tier && tier !== "basic") {
        await setMyTier(tier);
        await refreshProfile();
      }
      navigate("/portfolio", { replace: true });
    } catch {
      setError("Couldn't set your plan. Try again, or skip for now.");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen pt-28 bg-gray-50 text-gray-900 dark:bg-gradient-to-b dark:from-gray-900 dark:via-gray-950 dark:to-black dark:text-white">
      <Navbar />

      <div className="max-w-6xl mx-auto px-6 pb-16">
        <div className="text-center mb-10">
          <span className="inline-block text-[11px] font-bold uppercase tracking-[0.2em] text-emerald-500 mb-3">
            You're in 🎉 · Last step
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-[-0.02em]">
            Pick a plan to get started
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-3 max-w-2xl mx-auto">
            Everything's free while we finalize billing — switch anytime. Not
            sure? Start on Basic and upgrade whenever you want.
          </p>
        </div>

        {error && (
          <div className="mb-6 max-w-xl mx-auto px-4 py-3 rounded-xl bg-red-500/10 text-red-500 text-sm text-center">
            {error}
          </div>
        )}

        {loadingPlans ? (
          <div className="text-center text-gray-500 py-12">Loading plans…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch">
            {plans.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                selected={
                  plan.cta_type !== "contact_sales" &&
                  selectedTier === plan.tier_key
                }
                ctaLabel={ctaLabel(plan)}
                onSelect={onSelect}
              />
            ))}
          </div>
        )}

        {/* Continue / skip */}
        <div className="mt-10 flex flex-col items-center gap-4">
          <button
            type="button"
            disabled={busy}
            onClick={() => proceed(selectedTier)}
            className="px-8 py-3 rounded-xl bg-indigo-600 text-white font-semibold hover:bg-indigo-500 transition-colors disabled:opacity-60 disabled:cursor-wait"
          >
            {busy
              ? "Setting up…"
              : `Continue with ${TIER_LABEL[selectedTier] || "Basic"} →`}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => proceed("basic")}
            className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
          >
            Skip for now — I'll decide later
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChoosePlan;
